import * as React from "react";
import { Text, Stack, getTheme, mergeStyles, format } from "@fluentui/react";
import { mergeStyleSets } from "@fluentui/merge-styles";
import { Utils } from "../Utils";
import * as globalActions from "../actions/globalConfigActions";
import { useAppDispatch, useAppSelector } from "../hooks";

/**
 * The subject of the feedback mail message
 */
const feedbackMailSubject = "RCA Feedback";

/**
 * The Footer component
 *
 * @returns The component
 */
export const RcaFooter: React.FC = () => {
  const { language, appVersion, feedbackEmail, cookieConsentRequired } = useAppSelector((s) => s.globalConfig);
  const dispatch = useAppDispatch();

  const theme = getTheme();

  const footerFontStyle = mergeStyles(theme.fonts.small, {
    selectors: {
      [Utils.mobileQuery]: theme.fonts.xSmall,
    },
  });

  const footerLinkStyle = mergeStyles({
    color: theme.semanticColors.bodyText,
    textDecorationColor: "currentColor",
  });

  const classNames = mergeStyleSets({
    footerBlockStyle: {
      marginTop: 5,
      marginBottom: 5,
      marginRight: "5%",
      marginLeft: "5%",
      selectors: {
        [Utils.mobileQuery]: {
          height: 54,
        },
      },
    },
  });

  // this is a false positive in detecting the text is the same
  return (
    <Stack horizontal horizontalAlign="end">
      <div className={classNames.footerBlockStyle}>
        <Text styles={{ root: footerFontStyle }} variant="small">
          {format(WebsiteStrings.MSCopyrightText, new Date().getFullYear())}
        </Text>
        {" | "}
        <a className={footerLinkStyle} href="/Pages/ChangeList.htm" rel="noreferrer noopener" target="_blank">
          <Text styles={{ root: footerFontStyle }}>
            {WebsiteStrings.VersionTextVersionOnly} {appVersion}
          </Text>
        </a>
        {" | "}
        <a
          className={footerLinkStyle}
          // The random number generated here is for convenience. It doesn't matter if it's truly random
          href={`mailto:${feedbackEmail}?Subject=${feedbackMailSubject} ${Math.floor(Math.random() * 99999)}&body=${encodeURIComponent(
            ClientStrings.FeedbackEmailBody
          )}`}
        >
          <Text styles={{ root: footerFontStyle }}>{WebsiteStrings.FeedbackLinkText}</Text>
        </a>
        {" | "}
        <a className={footerLinkStyle} href="https://go.microsoft.com/fwlink/?LinkId=521839" rel="noreferrer noopener" target="_blank">
          <Text styles={{ root: footerFontStyle }}>{WebsiteStrings.PrivacyLinkText}</Text>
        </a>
        {cookieConsentRequired ? (
          <>
            {" | "}
            {/* It is common to use an anchor for this. Even the microsoft.com main site does the same in the footer */}
            {/* eslint-disable-next-line jsx-a11y/anchor-is-valid */}
            <a
              className={footerLinkStyle}
              href="#"
              onClick={() => void dispatch(globalActions.manageCookieConsent())}
              onKeyPress={() => void dispatch(globalActions.manageCookieConsent())}
              role="button"
              tabIndex={0}
            >
              <Text styles={{ root: footerFontStyle }}>{ClientStrings.ManageCookiesText}</Text>
            </a>
          </>
        ) : (
          <></>
        )}
        {" | "}
        <a className={footerLinkStyle} href="https://go.microsoft.com/fwlink/?linkid=2259814" rel="noreferrer noopener" target="_blank">
          <Text styles={{ root: footerFontStyle }}>{WebsiteStrings.ConsumerHealthPrivacyLinkText}</Text>
        </a>
        {" | "}
        <a className={footerLinkStyle} href="https://go.microsoft.com/fwlink/?LinkID=206977" rel="noreferrer noopener" target="_blank">
          <Text styles={{ root: footerFontStyle }}>{WebsiteStrings.TOULinkText}</Text>
        </a>
        {language !== "fr" && !language.startsWith("fr-") ? undefined : (
          <>
            {" | "}
            <a className={footerLinkStyle} href="https://go.microsoft.com/fwlink/?linkid=2121428" rel="noreferrer noopener" target="_blank">
              <Text styles={{ root: footerFontStyle }}>{WebsiteStrings.FrenchDecreeLinkText}</Text>
            </a>
          </>
        )}
        {language !== "it" && !language.startsWith("it-") ? undefined : (
          <>
            {" | "}
            <a
              className={footerLinkStyle}
              href="https://www.microsoft.com/it-it/accessibility/accessibilita/dichiarazioni"
              rel="noreferrer noopener"
              target="_blank"
            >
              <Text styles={{ root: footerFontStyle }}>{WebsiteStrings.ItalianAccessibilityText}</Text>
            </a>
          </>
        )}
      </div>
    </Stack>
  );
};
